import type { ApiClient } from "@/api/client";
import { API_VERSION_PREFIX, newIdempotencyKey } from "@/api/client";

const BASE = `${API_VERSION_PREFIX}/hq/knowledge`;

export type ArticleStatus = "draft" | "under_review" | "approved" | "published" | "superseded" | "archived";
export type ArticleAudience = "internal" | "public";
export type ArticleType =
  | "help_article"
  | "sop"
  | "policy"
  | "training"
  | "playbook"
  | "emergency_procedure"
  | "template";
export type ArticleAction =
  | "submit_review"
  | "request_changes"
  | "approve"
  | "publish"
  | "unpublish"
  | "supersede"
  | "archive"
  | "restore";

export interface ArticleDto {
  id: string;
  item_type: ArticleType;
  status: ArticleStatus;
  audience: ArticleAudience;
  title: string | null;
  slug: string | null;
  summary: string | null;
  body?: string | null;
  category: string | null;
  tags: string[];
  version: number;
  owner_user_id: string | null;
  owner_name: string | null;
  policy_id: string | null;
  review_due_at: string | null;
  review_overdue: boolean;
  published_at: string | null;
  view_count: number;
  helpful: number;
  not_helpful: number;
  created_at: string;
  updated_at: string;
  content_reference: string | null;
  submitted_by_user_id: string | null;
  created_by_name?: string | null;
  submitted_by_name?: string | null;
  approved_by_name?: string | null;
  published_by_name?: string | null;
}

export interface ArticleVersionRow {
  id: string;
  version: number;
  title: string | null;
  change_note: string | null;
  created_by_name: string | null;
  created_at: string;
}

export interface ArticleDetail {
  article: ArticleDto;
  versions: ArticleVersionRow[];
  feedback_comments: { helpful: boolean; comment: string; created_at: string }[];
  linked_ticket_count: number;
  viewer: { can_edit: boolean; can_publish: boolean; actions: ArticleAction[]; self_approval_blocked: boolean };
}

export interface KnowledgeSummary {
  by_status: Record<string, number>;
  categories: Record<string, number>;
  review_overdue: number;
  live_public: number;
  total: number;
}

export interface ArticleListParams {
  cursor?: string;
  limit?: number;
  sort?: string;
  status?: string;
  audience?: ArticleAudience;
  item_type?: string;
  category?: string;
  q?: string;
  review_overdue?: boolean;
}

function toQuery(params: object): string {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === "" || v === false) continue;
    s.set(k, String(v));
  }
  const qs = s.toString();
  return qs ? `?${qs}` : "";
}

export function createArticlesApi(client: ApiClient) {
  return {
    list(params: ArticleListParams = {}) {
      return client.get<{ items: ArticleDto[]; next_cursor: string | null; meta: { total?: number } }>(
        `${BASE}/articles${toQuery(params)}`,
      );
    },
    summary() {
      return client.get<KnowledgeSummary>(`${BASE}/summary`);
    },
    categories() {
      return client.get<{ categories: { name: string; count: number }[] }>(`${BASE}/categories`);
    },
    get(id: string) {
      return client.get<ArticleDetail>(`${BASE}/articles/${id}`);
    },
    create(body: Record<string, unknown>) {
      return client.post<{ article: ArticleDto }>(`${BASE}/articles`, body, newIdempotencyKey());
    },
    update(id: string, body: Record<string, unknown>) {
      return client.patch<{ article: ArticleDto }>(`${BASE}/articles/${id}`, body, newIdempotencyKey());
    },
    action(id: string, action: ArticleAction, note?: string) {
      return client.post<{ article: ArticleDto }>(
        `${BASE}/articles/${id}/actions`,
        { action, ...(note ? { note } : {}) },
        newIdempotencyKey(),
      );
    },
    version(id: string, version: number) {
      return client.get<{ version: { version: number; title: string | null; summary: string | null; body: string | null; change_note: string | null; created_at: string } }>(
        `${BASE}/articles/${id}/versions/${version}`,
      );
    },
    restore(id: string, version: number, expectedVersion: number) {
      return client.post<{ article: ArticleDto }>(
        `${BASE}/articles/${id}/restore-version`,
        { version, expected_version: expectedVersion },
        newIdempotencyKey(),
      );
    },
    usage(id: string) {
      return client.get<{ tickets: { support_ticket_id: string; created_at: string; support_ticket: { public_id: string; status: string; contact_reason: string | null } | null }[] }>(
        `${BASE}/articles/${id}/usage`,
      );
    },
    linkTicket(id: string, ticketId: string) {
      return client.post<{ linked: boolean; already: boolean }>(`${BASE}/articles/${id}/link-ticket`, { ticket_id: ticketId }, newIdempotencyKey());
    },
  };
}
